import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  updateEmail,
  updatePassword
} from 'firebase/auth';
import { auth, googleProvider, db, doc, setDoc, getDoc } from '../firebase';

// Admin emails — add your admin email(s) here
const ADMIN_EMAILS = ['noveltymkt@gmail.com', 'novetymkt@gmail.com', 'noveltyrmkt@gmail.com', 'ph.souza.roque@gmail.com'];


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'client'
  const [userProfile, setUserProfile] = useState({}); // Stores extra firestore data
  const [idToken, setIdToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep token fresh — Firebase tokens expire after 1h
  const refreshToken = async (firebaseUser) => {
    if (!firebaseUser) return null;
    const token = await firebaseUser.getIdToken(true);
    setIdToken(token);
    return token;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const token = await refreshToken(firebaseUser);
        const role = ADMIN_EMAILS.some(e => e.toLowerCase() === firebaseUser.email?.toLowerCase()) ? 'admin' : 'client';
        
        let profileData = {};
        try {
          const docSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (docSnap.exists()) {
            profileData = docSnap.data();
          }
        } catch (e) {
          console.warn('Warning fetching user profile:', e.message);
        }

        setUser(firebaseUser);
        setUserRole(role);
        setUserProfile(profileData);
        setIdToken(token);
      } else {
        setUser(null);
        setUserRole(null);
        setUserProfile({});
        setIdToken(null);
      }
      setLoading(false);
    });

    // Refresh token every 55 minutes (before 1h expiry)
    const tokenRefreshInterval = setInterval(() => {
      if (auth.currentUser) refreshToken(auth.currentUser);
    }, 55 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  const loginWithEmail = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    setIdToken(token);
    return cred.user;
  };

  const registerWithEmail = async (email, password, whatsapp) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    setIdToken(token);
    // Save profile with WhatsApp to Firestore
    try {
      await setDoc(doc(db, 'users', cred.user.uid), {
        email,
        whatsapp: whatsapp || '',
        created_at: new Date().toISOString(),
      });
    } catch (e) {
      console.error('Failed to save user profile:', e);
    }
    return cred.user;
  };

  const loginWithGoogle = async () => {
    const cred = await signInWithPopup(auth, googleProvider);
    const token = await cred.user.getIdToken();
    setIdToken(token);
    return cred.user;
  };

  const updateUserProfile = async (displayName, photoURL, email, whatsapp, password) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    
    // Update Auth Profile
    if (displayName !== auth.currentUser.displayName || photoURL !== auth.currentUser.photoURL) {
      await updateProfile(auth.currentUser, { displayName, photoURL });
    }
    
    // Update Email
    if (email && email !== auth.currentUser.email) {
      await updateEmail(auth.currentUser, email);
    }

    // Update Password
    if (password) {
      await updatePassword(auth.currentUser, password);
    }
    
    // Update Firestore Profile (whatsapp, etc)
    const newProfileData = { ...userProfile, whatsapp, email };
    await setDoc(doc(db, 'users', auth.currentUser.uid), newProfileData, { merge: true });
    
    setUser({ ...auth.currentUser, displayName, photoURL, email });
    setUserProfile(newProfileData);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserRole(null);
    setIdToken(null);
  };

  const getToken = async () => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken();
  };

  return (
    <AuthContext.Provider value={{ user, userRole, userProfile, idToken, loading, loginWithEmail, registerWithEmail, loginWithGoogle, updateUserProfile, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
